import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { dateTime } from '../lib/format.js';
import { ErrorState, Icon, Loading, PageHeader, Panel } from '../components/ui.js';
import '../screens-2b.css';
import { Diagram } from '../components/flowmap/Diagram.js';
import { SidePanel } from '../components/flowmap/SidePanel.js';
import type { PanelState } from '../components/flowmap/SidePanel.js';
import { kindActive, neighbourhood, tracePath, walkthrough } from '../components/flowmap/model.js';
import type {
  Active,
  CommTab,
  FlowData,
  JourneyStep,
  Kind,
  Rule,
} from '../components/flowmap/model.js';

const KINDS: Array<{ id: Kind; text: string }> = [
  { id: 'all', text: 'Vše' },
  { id: 'service', text: 'Servis' },
  { id: 'marketing', text: 'Marketing' },
];

/** Příklad ze zadání: zákazník koupil tolik dní před závodem. */
const EXAMPLE_LEAD_DAYS = 3;

/**
 * Schéma toku: kudy data v hubu tečou od e-shopu až po reakci v SmartEmailingu,
 * s živými čísly, filtrem servis / marketing, trasou jedné zprávy a příkladem.
 */
export function FlowMap({
  onNavigate,
  focus,
}: {
  onNavigate: (tab: CommTab) => void;
  /** Chip z lišty „Jak to celé funguje“ — zvýrazní a otevře uzly. */
  focus?: { ids: string[]; seq: number };
}): JSX.Element {
  const [kind, setKind] = useState<Kind>('all');
  const [days, setDays] = useState<7 | 30>(30);
  const [trace, setTrace] = useState('');
  const [hover, setHover] = useState<string | null>(null);
  const [panel, setPanel] = useState<PanelState>(null);
  const [walk, setWalk] = useState<number | null>(null);
  const [focusIds, setFocusIds] = useState<string[]>([]);
  const nodes = useRef(new Map<string, SVGGElement>());
  const closeRef = useRef<HTMLButtonElement>(null);
  const lastOpener = useRef<string | null>(null);
  const scroller = useRef<HTMLDivElement>(null);

  const flow = useQuery<FlowData>({
    queryKey: ['flow-map', days],
    queryFn: () => api.get(`/api/flow-map?days=${days}`),
  });
  const catalogue = useQuery({
    queryKey: ['flow-catalogue'],
    queryFn: () => api.get<{ published: { rules: Rule[] } | null }>('/api/catalogue'),
  });
  const journey = useQuery({
    queryKey: ['journey', EXAMPLE_LEAD_DAYS],
    queryFn: () =>
      api.get<{ steps: JourneyStep[] }>(`/api/catalogue/journey?leadDays=${EXAMPLE_LEAD_DAYS}`),
    enabled: walk !== null,
  });

  useEffect(() => {
    if (!focus || focus.ids.length === 0) return;
    setFocusIds(focus.ids);
    setTrace('');
    setWalk(null);
    lastOpener.current = null;
    setPanel({ type: 'node', id: focus.ids[0] as string });
  }, [focus]);

  const rulesFor = useCallback(
    (code: string): Rule[] =>
      (catalogue.data?.published?.rules ?? []).filter((r) => r.code === code),
    [catalogue.data],
  );

  const close = useCallback(() => {
    setPanel(null);
    setFocusIds([]);
    const id = lastOpener.current;
    if (id) nodes.current.get(id)?.focus();
  }, []);

  useEffect(() => {
    if (!panel) return;
    closeRef.current?.focus({ preventScroll: true });
    // Panel překrývá pravý okraj: uzel, který by zakryl, posuneme doleva.
    const el = panel.type === 'node' ? nodes.current.get(panel.id) : null;
    const box = scroller.current;
    if (el && box && window.innerWidth > 700) {
      const over = el.getBoundingClientRect().right - (window.innerWidth - 440);
      if (over > 0) box.scrollBy({ left: over, behavior: 'smooth' });
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [panel, close]);

  const steps = useMemo(
    () =>
      flow.data && journey.data
        ? walkthrough(
            EXAMPLE_LEAD_DAYS,
            journey.data.steps,
            flow.data.settings,
            catalogue.data?.published?.rules ?? [],
          )
        : null,
    [flow.data, journey.data, catalogue.data],
  );

  if (flow.isLoading) return <Loading what="schéma toku" />;
  if (flow.isError)
    return (
      <ErrorState
        title="Schéma toku se nepodařilo načíst"
        error={flow.error}
        onRetry={() => void flow.refetch()}
      />
    );
  const data = flow.data;
  if (!data) return <Loading />;

  const step = walk !== null ? steps?.[walk] : undefined;
  const selectedId = panel?.type === 'node' ? panel.id : null;

  let active: Active | null;
  if (step) active = neighbourhood(step.nodes);
  else if (hover) active = neighbourhood([hover]);
  else if (trace) active = tracePath(rulesFor(trace)).active;
  else if (focusIds.length > 0) active = neighbourhood(focusIds);
  else if (selectedId) active = neighbourhood([selectedId]);
  else active = kindActive(kind);

  const select = (id: string): void => {
    lastOpener.current = id;
    setFocusIds([]);
    setPanel({ type: 'node', id });
  };
  const pickTrace = (code: string): void => {
    setTrace(code);
    setWalk(null);
    setFocusIds([]);
    setPanel(code ? { type: 'trace', code } : null);
  };
  const startWalk = (): void => {
    setPanel(null);
    setTrace('');
    setFocusIds([]);
    setWalk(0);
  };
  const last = (steps?.length ?? 0) - 1;

  return (
    <>
      <PageHeader
        level={2}
        title="Schéma toku"
        description="Kudy data tečou: zleva doprava od e-shopu a webu přes příjem, cesty zákazníků, plánování a rozhodnutí až k odeslání a reakcím. Tloušťka čáry odpovídá objemu za zvolené období. Klikněte na krok pro vysvětlení, pravidla a živá čísla."
      />

      <Panel
        title="Cesta dat v hubu"
        icon="route"
        subtitle={`Stav k ${dateTime(data.generatedAt)} · za posledních ${days} dní`}
        action={
          <div className="fm-tools">
            <div className="seg" role="group" aria-label="Druh zpráv">
              {KINDS.map((k) => (
                <button
                  key={k.id}
                  type="button"
                  className={kind === k.id ? 'on' : ''}
                  aria-pressed={kind === k.id}
                  onClick={() => setKind(k.id)}
                >
                  {k.text}
                </button>
              ))}
            </div>
            <div className="seg" role="group" aria-label="Období">
              {([7, 30] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  className={days === d ? 'on' : ''}
                  aria-pressed={days === d}
                  onClick={() => setDays(d)}
                >
                  {d} dní
                </button>
              ))}
            </div>
          </div>
        }
      >
        <div className="fm-bar2">
          <label className="fm-trace">
            <span>Cesta jedné zprávy</span>
            <select value={trace} onChange={(e) => pickTrace(e.target.value)}>
              <option value="">— žádná —</option>
              {data.byCode.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} · {c.name}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="btn" onClick={startWalk}>
            <Icon name="bolt" size={15} /> Příklad: zákazník koupil lístek {EXAMPLE_LEAD_DAYS} dny
            před závodem
          </button>
          <ul className="fm-legend" aria-label="Legenda">
            <li>
              <i className="sw k-service" /> servis → přímo z hubu
            </li>
            <li>
              <i className="sw k-marketing" /> marketing → šablona ve SmartEmailingu
            </li>
            <li>
              <i className="sw k-all" /> společná cesta
            </li>
            <li>
              <i className="sw soon" /> připravuje se
            </li>
          </ul>
        </div>

        {walk !== null ? (
          <div className="fm-walk" role="status" aria-live="polite">
            {!step ? (
              journey.isError ? (
                <ErrorState title="Příklad se nepodařilo načíst" error={journey.error} />
              ) : (
                <Loading what="příklad" />
              )
            ) : (
              <>
                <div className="fm-walk-head">
                  <b>
                    Krok {walk + 1} z {steps?.length}: {step.title}
                  </b>
                  <span>
                    <button className="btn" disabled={walk === 0} onClick={() => setWalk(walk - 1)}>
                      Zpět
                    </button>
                    <button
                      className="btn pri"
                      onClick={() => setWalk(walk >= last ? null : walk + 1)}
                    >
                      {walk >= last ? 'Hotovo' : 'Další krok'}
                    </button>
                    <button className="btn" onClick={() => setWalk(null)}>
                      Zavřít
                    </button>
                  </span>
                </div>
                <p>{step.text}</p>
              </>
            )}
          </div>
        ) : null}

        <div
          ref={scroller}
          className={`fm-scroll${panel ? ' pad' : ''}`}
          role="region"
          aria-label="Schéma toku dat, lze posouvat do stran"
        >
          <Diagram
            data={data}
            kind={kind}
            active={active}
            selected={selectedId}
            onSelect={select}
            onHover={setHover}
            registerNode={(id, el) => {
              if (el) nodes.current.set(id, el);
              else nodes.current.delete(id);
            }}
          />
        </div>
        <p className="muted fm-hint">
          Přerušovaná čára (zpětná vazba) a uzly s označením „připravuje se“ patří do fáze 2.
          Duplicitní události hub při příjmu zahazuje, ale nepočítá.
        </p>
      </Panel>

      {panel ? (
        <SidePanel
          ref={closeRef}
          state={panel}
          data={data}
          kind={kind}
          rules={panel.type === 'trace' ? rulesFor(panel.code) : []}
          onClose={close}
          onNavigate={(tab) => {
            setPanel(null);
            onNavigate(tab);
          }}
        />
      ) : null}
    </>
  );
}
